import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button } from "react-bootstrap";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    PieChart, Pie, Cell,
    Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer
} from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const FavGraph = ({theme}) => {
    const [tvFavorites, setTvFavorites] = useState([]);
    const [movieFavorites, setMovieFavorites] = useState([]);
    const [movieHistory, setMovieHistory] = useState([]);
    const [tvHistory, setTvHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        try {
            setLoading(true);
            const db = getFirestore();
            const userFavoritesCollection = collection(db, 'userFavorite');
            const historyCollection = collection(db, 'history');

            const [favoritesSnapshot, historySnapshot] = await Promise.all([
                getDocs(userFavoritesCollection),
                getDocs(historyCollection)
            ]);

            const tvFavorites = [];
            const movieFavorites = [];
            const movieHistory = [];
            const tvHistory = [];

            // Process favorites data
            favoritesSnapshot.forEach(doc => {
                const data = doc.data();
                Object.keys(data.favorites || {}).forEach(favoriteId => {
                    const favorite = {
                        userId: doc.id,
                        favoriteId,
                        favoriteDetails: data.favorites[favoriteId]
                    };
                    if (data.favorites[favoriteId].mediaType === 'tv') {
                        tvFavorites.push(favorite);
                    } else if (data.favorites[favoriteId].mediaType === 'movie') {
                        movieFavorites.push(favorite);
                    }
                });
            });

            // Process history data
            historySnapshot.forEach(doc => {
                const data = doc.data();
                (data.movies || []).forEach(movie => {
                    movieHistory.push(movie);
                });
                (data.tvShows || []).forEach(tvShow => {
                    tvHistory.push(tvShow);
                });
            });

            setTvFavorites(tvFavorites);
            setMovieFavorites(movieFavorites);
            setMovieHistory(movieHistory);
            setTvHistory(tvHistory);
        } catch (error) {
            console.error("Error fetching data: ", error);
            setError("Error fetching data. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const generateChartData = (favorites) => {
        const countMap = {};
        favorites.forEach(favorite => {
            const { mediaId, mediaTitle } = favorite.favoriteDetails;
            const name = mediaTitle || mediaId; // Use mediaTitle if available, otherwise fallback to mediaId
            countMap[name] = countMap[name] ? countMap[name] + 1 : 1;
        });
        return Object.keys(countMap).map(name => ({
            name,
            count: countMap[name]
        }));
    };

    const generateHistoryChartData = (history) => {
        const countMap = {};
        history.forEach(entry => {
            const { movieId, mediaTitle } = entry;
            const name = mediaTitle || movieId; // Use mediaTitle if available, otherwise fallback to movieId
            countMap[name] = countMap[name] ? countMap[name] + 1 : 1;
        });
        return Object.keys(countMap).map(name => ({
            name,
            count: countMap[name]
        }));
    };

    // Combine movie and TV history for genre analysis
    const combinedHistory = [...movieHistory, ...tvHistory];

    // Generate genre count map
    const genreCountMap = {};
    combinedHistory.forEach(entry => {
        const { genres } = entry;
        genres.forEach(genre => {
            if (genreCountMap[genre]) {
                genreCountMap[genre] += 1;
            } else {
                genreCountMap[genre] = 1;
            }
        });
    });

    // Convert genre count map to array for radar chart
    const genreChartData = Object.keys(genreCountMap).map(genre => ({
        name: genre,
        count: genreCountMap[genre]
    }));

    const maxHistoryCount = Math.max(...genreChartData.map(entry => entry.count));

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', '#FF1919']; // Define colors for pie chart segments

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
        const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));

        return (
            <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central">
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    // Calculate summaries
    const maxTvHistory = Math.max(...generateHistoryChartData(tvHistory).map(entry => entry.count), 0);
    const maxMovieHistory = Math.max(...generateHistoryChartData(movieHistory).map(entry => entry.count), 0);
    const maxTvFavorites = Math.max(...generateChartData(tvFavorites).map(entry => entry.count), 0);
    const maxMovieFavorites = Math.max(...generateChartData(movieFavorites).map(entry => entry.count), 0);

    // Get names of top movies and TV shows
    const topMovieHistory = generateHistoryChartData(movieHistory).filter(entry => entry.count === maxMovieHistory).map(entry => entry.name);
    const topTvHistory = generateHistoryChartData(tvHistory).filter(entry => entry.count === maxTvHistory).map(entry => entry.name);
    const topMovieFavorites = generateChartData(movieFavorites).filter(entry => entry.count === maxMovieFavorites).map(entry => entry.name);
    const topTvFavorites = generateChartData(tvFavorites).filter(entry => entry.count === maxTvFavorites).map(entry => entry.name);
    const mostViewedGenre = genreChartData.reduce((max, current) => (current.count > max.count ? current : max), { count: 0 });

    // Summaries
    const summaries = [
        {
            title: 'Most Viewed TV Shows',
            description: `The most viewed TV show is ${topTvHistory.join(', ')} with ${maxTvHistory} views.`,
        },
        {
            title: 'Most Viewed Movies',
            description: `The most viewed movie is ${topMovieHistory.join(', ')} with ${maxMovieHistory} views.`,
        },
        {
            title: 'Most Liked TV Shows',
            description: `The most liked TV show is ${topTvFavorites.join(', ')} with ${maxTvFavorites} likes.`,
        },
        {
            title: 'Most Liked Movies',
            description: `The most liked movie is ${topMovieFavorites.join(', ')} with ${maxMovieFavorites} likes.`,
        },
        {
            title: 'Most Viewed Genres',
            description: `The most viewed genre is ${mostViewedGenre.name} with ${mostViewedGenre.count} views.`,
        },
    ];

    const generatePDF = async () => {
        const input = document.getElementById('pdf-content');
        const canvas = await html2canvas(input, {
            scrollY: -window.scrollY,
            backgroundColor: 'black' // Set background color to black
        });

        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        let pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        // Adjust pdfHeight if it exceeds page height
        const pageHeight = pdf.internal.pageSize.getHeight();
        if (pdfHeight > pageHeight) {
            pdfHeight = pageHeight;
        }

        // Add black background to the first page
        pdf.setFillColor(0);
        pdf.rect(0, 0, pdfWidth, pdfHeight, 'F'); // Fill background with black

        // Add graphs
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);



        pdf.save('favorites_viewing_history.pdf');
    };




    return (
        <div className={`container ${theme}`}>
            <Container fluid className="mt-5">
                <div id="pdf-content">
                    <h2 className="mb-4 text-center" style={{ color: theme === "dark" ? "white" : "black" }}>
                        USER'S FAVORITE and MOSTLY VIEWED CONTENT GRAPHS
                    </h2>
                    <Row className="justify-content-center mb-4">
                        <Button onClick={generatePDF} variant="primary" style={{ width: '150px', backgroundColor: "white", color: "black" }}>
                            Generate PDF
                        </Button>
                    </Row>

                    <Row className="justify-content-center">
                        <Col xs={12} lg={6}>
                            <h3 className="text-center" style={{ color: theme === "dark" ? "white" : "black" }}>TV Favorites</h3>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={generateChartData(tvFavorites)}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        interval={0}
                                        angle={-45}
                                        textAnchor="end"
                                        height={120}
                                        tick={{ fontSize: 10 }}
                                        tickFormatter={(name) => name.length > 20 ? `${name.substring(0, 20)}...` : name}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="count" fill="#8884d8" />
                                </BarChart>
                            </ResponsiveContainer>

                            <ResponsiveContainer width="100%" height={400}>
                                <PieChart>
                                    <Pie
                                        data={generateChartData(tvFavorites)}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={renderCustomizedLabel}
                                        outerRadius={120}
                                        fill="#8884d8"
                                        dataKey="count"
                                    >
                                        {generateChartData(tvFavorites).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Col>

                        <Col xs={12} lg={6}>
                            <h3 className="text-center" style={{ color: theme === "dark" ? "white" : "black" }}>Movie Favorites</h3>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={generateChartData(movieFavorites)}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        interval={0}
                                        angle={-45}
                                        textAnchor="end"
                                        height={120}
                                        tick={{ fontSize: 10 }}
                                        tickFormatter={(name) => name.length > 20 ? `${name.substring(0, 20)}...` : name}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="count" fill="#82ca9d" />
                                </BarChart>
                            </ResponsiveContainer>

                            <ResponsiveContainer width="100%" height={500}>
                                <PieChart>
                                    <Pie
                                        data={generateChartData(movieFavorites)}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={renderCustomizedLabel}
                                        outerRadius={120}
                                        fill="#82ca9d"
                                        dataKey="count"
                                    >
                                        {generateChartData(movieFavorites).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Col>
                    </Row>

                    <Row className="justify-content-center mt-4">
                        <Col xs={12} lg={6}>
                            <h3 className="text-center" style={{ color: theme === "dark" ? "white" : "black" }}>Most Viewed TV Shows</h3>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={generateHistoryChartData(tvHistory)}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        interval={0}
                                        angle={-45}
                                        textAnchor="end"
                                        height={120}
                                        tick={{ fontSize: 10 }}
                                        tickFormatter={(name) => name.length > 20 ? `${name.substring(0, 20)}...` : name}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="count" fill="#8884d8" />
                                </BarChart>
                            </ResponsiveContainer>

                            <ResponsiveContainer width="100%" height={600}>
                                <PieChart>
                                    <Pie
                                        data={generateHistoryChartData(tvHistory)}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={renderCustomizedLabel}
                                        outerRadius={120}
                                        fill="#8884d8"
                                        dataKey="count"
                                    >
                                        {generateHistoryChartData(tvHistory).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Col>

                        <Col xs={12} lg={6}>
                            <h3 className="text-center" style={{ color: theme === "dark" ? "white" : "black" }}>Most Viewed Movies</h3>
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart data={generateHistoryChartData(movieHistory)}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        interval={0}
                                        angle={-45}
                                        textAnchor="end"
                                        height={120}
                                        tick={{ fontSize: 10 }}
                                        tickFormatter={(name) => name.length > 20 ? `${name.substring(0, 20)}...` : name}
                                    />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="count" fill="#82ca9d" />
                                </BarChart>
                            </ResponsiveContainer>

                            <ResponsiveContainer width="100%" height={1100}>
                                <PieChart>
                                    <Pie
                                        data={generateHistoryChartData(movieHistory)}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={renderCustomizedLabel}
                                        outerRadius={140}
                                        fill="#82ca9d"
                                        dataKey="count"
                                    >
                                        {generateHistoryChartData(movieHistory).map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </Col>
                    </Row>

                    <Row className="justify-content-center mt-4">
                        <Col xs={12} lg={6}>
                            <h3 className="text-center" style={{ color: theme === "dark" ? "white" : "black" }}>Most Viewed Genres</h3>
                            <ResponsiveContainer width="100%" height={350}>
                                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={genreChartData}>
                                    <PolarGrid />
                                    <PolarAngleAxis dataKey="name" />
                                    <PolarRadiusAxis angle={30} domain={[0, maxHistoryCount]} />
                                    <Radar name="Genres" dataKey="count" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                                    <Tooltip />
                                </RadarChart>
                            </ResponsiveContainer>
                        </Col>
                    </Row>

                    <Row className="justify-content-center mt-4">
                        <Col xs={12}>
                            <h3 className="text-center" style={{ color: theme === "dark" ? "white" : "black" }}>Summaries</h3>
                            <Container className="mt-4 mb-4">
                                {summaries.map((summary, index) => (
                                    <Row key={index} className="mb-3">
                                        <Col>
                                            <ul style={{ color: theme === "dark" ? "white" : "black", listStyleType: "none", padding: 0 }}>
                                                <li style={{ color: theme === "dark" ? "white" : "black" }}>
                                                    <strong>{summary.title}</strong>: {summary.description}
                                                </li>
                                            </ul>
                                        </Col>
                                    </Row>
                                ))}
                            </Container>
                        </Col>
                    </Row>

                    {loading && <p style={{ color: theme === "dark" ? "white" : "black" }}>Loading...</p>}
                    {error && <p style={{ color: theme === "dark" ? "white" : "black" }}>{error}</p>}
                </div>
            </Container>
        </div>
    );
};

export default FavGraph;